import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { TicketPercent, X } from 'lucide-react';
import { shopAPI } from '../lib/api';
import { t } from '../lib/i18n';

// Back from Stripe without paying (?checkout=cancelled): free the member's held
// coupon right away instead of waiting for the reservation to lapse.
export function useReleaseOnCancel() {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('checkout') !== 'cancelled') return;
    shopAPI.releaseCoupon()
      .then(({ data }) => { if (data.released) toast(t('Payment cancelled — your coupon is available again.')); })
      .catch(() => {});
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('checkout');
      return next;
    }, { replace: true });
  }, [params, setParams]);
}

// s136 checkout coupon (cart + course purchase card). Collapsed to a link until
// needed; "Apply" previews the discount server-side (no reservation). The
// parent keeps the applied result and passes `applied.code` to checkout.
export default function CouponField({ courseIds, applied, onApplied, compact = false }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await shopAPI.validateCoupon(code, courseIds);
      onApplied(data);
    } catch (err) {
      setError(err.response?.data?.detail || t('Could not apply the coupon'));
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-[10px] border border-dashed border-lms-ink px-3.5 py-2.5">
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <TicketPercent size={16} className="shrink-0 text-lms-ink" />
          <span className="truncate font-mono font-bold text-lms-ink">{applied.code}</span>
          <span className="shrink-0 text-lms-sub lms-num">−{applied.percent}%</span>
        </span>
        <button
          type="button"
          onClick={() => { onApplied(null); setCode(''); }}
          aria-label={t('Remove coupon')}
          className="lms-press flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lms-sub hover:bg-lms-raised hover:text-lms-ink"
        >
          <X size={15} />
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 self-start text-sm font-semibold text-lms-link hover:underline ${compact ? '' : 'py-1'}`}
      >
        <TicketPercent size={15} /> {t('Have a coupon?')}
      </button>
    );
  }

  return (
    <form onSubmit={apply} className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
          placeholder={t('Coupon code')}
          aria-label={t('Coupon code')}
          aria-invalid={!!error}
          autoFocus
          maxLength={40}
          className="h-11 min-w-0 flex-1 rounded-[10px] border border-lms-line bg-lms-input px-3.5 font-mono text-sm uppercase text-lms-ink outline-none focus:border-[var(--lms-focus-border)]"
        />
        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="lms-press h-11 shrink-0 rounded-[10px] border border-lms-ink px-4 text-sm font-bold text-lms-ink hover:bg-lms-raised disabled:opacity-50"
        >
          {busy ? t('Checking…') : t('Apply')}
        </button>
      </div>
      {error && <span role="alert" className="text-[12.5px] text-lms-sale">{t(error)}</span>}
    </form>
  );
}
